import { useEffect, useMemo, useState } from "react";
import {
  TableColumns,
  TableColumnWidth,
  TableData,
  TextBox,
} from "@veneer/core";
import { CheckInIcon } from "../../features/checkInVsAbsence/components/CheckInIcon";
import { TFunction, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import userService from "../../services/generalApi/user.service";
import { IModalMessage } from "../../models/dto/generalApi/ICheckInVsAbsence";

export const UseCheckInVsAbsence = (
  selectedMonth: number,
  selectedYear: number,
  setLoadingCheckInVsAbsence: (loading: boolean) => void,
  setModalMessage: React.Dispatch<React.SetStateAction<IModalMessage>>
) => {
  const [originalData, setOriginalData] = useState<TableData[]>([]);
  const [searchUser, setSearchUser] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [loading, setLoading] = useState(false);
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const [columns, setColumns] = useState<TableColumns[]>([]);
  const [columnsWidth, setColumnsWidth] = useState<TableColumnWidth[]>([]);
  const [debouncedSearchUser, setDebouncedSearchUser] =
    useState<string>(searchUser);

  const [t] = useTranslation("global");
  const languageId: any = useSelector((state: RootState) => state.languageId);

  const years = Array.from({ length: 2 }, (_, i) => currentYear - i);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const months = [
    { value: 1, label: t("header.january") },
    { value: 2, label: t("header.february") },
    { value: 3, label: t("header.march") },
    { value: 4, label: t("header.april") },
    { value: 5, label: t("header.may") },
    { value: 6, label: t("header.june") },
    { value: 7, label: t("header.july") },
    { value: 8, label: t("header.august") },
    { value: 9, label: t("header.september") },
    { value: 10, label: t("header.october") },
    { value: 11, label: t("header.november") },
    { value: 12, label: t("header.december") },
  ];

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchUser(searchUser);
    }, 500); // 300ms debounce

    return () => {
      clearTimeout(handler); // Limpia el timeout anterior si el usuario sigue escribiendo
    };
  }, [searchUser]);

  const filteredMonths = useMemo(() => {
    if (selectedYear === currentYear) {
      return months.filter((month) => month.value <= currentMonth);
    }
    return months;
  }, [selectedYear, currentYear, currentMonth, months]);

  const handleChangeSearchUser = (value: string) => {
    setSearchUser(value);
    setCurrentPage(1);
  };

  const searchTextBox = useMemo(
    () => (
      <TextBox
        className="searchUserCheckInVsAbsence"
        id="TextboxUser"
        label={t("header.search")}
        type="search"
        onChange={handleChangeSearchUser}
        value={searchUser}
      />
    ),
    [searchUser, t]
  );

  const columnConfigMap = useMemo(
    () => ({
      DynamicMonthlyTable: (
        year: number,
        month: number,
        t: TFunction
      ): TableColumns[] => {
        const daysInMonth = new Date(year, month, 0).getDate();
        const columns: TableColumns[] = [
          {
            id: "nameOfUser",
            label: t("header.name"),
            filter: searchTextBox,
          },
        ];

        for (let day = 1; day <= daysInMonth; day++) {
          columns.push({ id: `day_${day}`, label: `${day}` });
        }

        return columns;
      },
    }),
    [searchTextBox]
  );

  const getTableColumns = (
    name: "DynamicMonthlyTable",
    year: number,
    month: number,
    t: TFunction
  ): TableColumns[] => {
    const config = columnConfigMap[name];
    if (typeof config === "function") {
      return config(year, month, t);
    }
    return [];
  };

  const generateColumnsWidth = (
    year: number,
    month: number
  ): TableColumnWidth[] => {
    const daysInMonth = new Date(year, month, 0).getDate();

    const nameOfUserWidth = 160;
    const totalWidth = 1200;
    const remainingWidth = totalWidth - nameOfUserWidth;
    const spacingBetweenDays = 35; // espacio visual extra
    const dayWidth =
      Math.floor(remainingWidth / daysInMonth + spacingBetweenDays) + "px";

    const widths: TableColumnWidth[] = [
      {
        columnId: "nameOfUser",
        width: `${nameOfUserWidth}px`,
        minWidth: `${nameOfUserWidth}px`,
        maxWidth: `${nameOfUserWidth}px`,
      },
    ];

    for (let day = 1; day <= daysInMonth; day++) {
      widths.push({
        columnId: `day_${day}`,
        width: dayWidth,
        minWidth: dayWidth,
        maxWidth: dayWidth,
      });
    }

    return widths;
  };

  const dynamicCols = useMemo(() => {
    return getTableColumns(
      "DynamicMonthlyTable",
      selectedYear,
      selectedMonth,
      t
    );
  }, [selectedYear, selectedMonth, t, columnConfigMap]);

  useEffect(() => {
    setColumns(dynamicCols);
  }, [dynamicCols]);

  const capitalizeName = (name: string) => {
    return name
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  useEffect(() => {
    const dynamicWidth = generateColumnsWidth(selectedYear, selectedMonth);
    setColumnsWidth(dynamicWidth);

    getData(selectedYear, selectedMonth);
  }, [selectedMonth, selectedYear, t, languageId]);

  const getData = async (year: number, month: number) => {
    setOriginalData([]);
    setLoading(true);
    try {
      const response = await userService.checkInVsAbsence(year, month);

      const formattedResponse = response.map((item: any) => ({
        ...item,
        nameOfUser: capitalizeName(item.nameOfUser),
      }));

      setOriginalData(formattedResponse);
    } catch (error) {
      setLoading(false);
      console.error("Error:", error);
      throw error;
    }
    setLoading(false);
  };

  async function onClickCheckInVsAbsence(
    year: number,
    month: number,
    languageId: number
  ): Promise<void> {
    setLoadingCheckInVsAbsence(true);
    try {
      const result = await userService.checkInVsAbsenceExcel(
        year,
        month,
        languageId
      );
      if (result instanceof Blob) {
        const url = window.URL.createObjectURL(result);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "checkInVsAbsence.xlsx");
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
      } else {
        setModalMessage({
          show: true,
          message: t("header.unexpectedResponse"),
          title: t("header.error"),
          onClose: () =>
            setModalMessage((prevState) => ({ ...prevState, show: false })),
        });
      }
    } catch (error) {
      setModalMessage({
        show: true,
        message: t("header.errorDownloadingFile"),
        title: t("header.error"),
        onClose: () =>
          setModalMessage((prevState) => ({ ...prevState, show: false })),
      });
    } finally {
      setLoadingCheckInVsAbsence(false);
    }
  }

  const tableData = useMemo(() => {
    if (!debouncedSearchUser) return originalData;

    return originalData.filter((element) =>
      element.nameOfUser
        .toLowerCase()
        .includes(debouncedSearchUser.toLowerCase())
    );
  }, [debouncedSearchUser, originalData]);

  const pagedData = useMemo(() => {
    return tableData
      .slice(
        (currentPage - 1) * pageSize,
        (currentPage - 1) * pageSize + pageSize
      )
      .map((row) => {
        const newRow: any = { ...row };
        Object.keys(newRow).forEach((key) => {
          if (key.startsWith("day_") && typeof newRow[key] === "number") {
            newRow[key] = <CheckInIcon value={newRow[key]} />;
          }
        });
        return newRow;
      });
  }, [tableData, currentPage, pageSize]);

  return {
    columns,
    columnsWidth,
    tableData,
    pagedData,
    currentPage,
    pageSize,
    filteredMonths,
    years,
    loading,
    setCurrentPage,
    setPageSize,
    onClickCheckInVsAbsence,
    originalData,
  };
};
